import React, { forwardRef, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';
import _ from 'lodash'

const CobaltMappingForm = forwardRef(function CobaltMappingForm(props, ref) {
  const editModeRef = useRef(!_.isEmpty(props.record));
  const [form] = Form.useForm();
  const [organisationList,setOrganisationList] = useState(() => {
    if (props.record) {
      const { organizationId, organizationName, organizationToken } = props.record;
      return [{id: organizationId, name: organizationName, otherProperties: { organizationToken }}]
    }
    return [];
  })
  const [organisationToken, setOrganisationToken] = useState(()=>{
    if (props.record) {
      const { organizationToken  } = props.record;
      return organizationToken;
    }
    return '';
  });
    const [pentestList, setPentestList] = useState(() => {
      if (props.record && !_.isNull(props.record.pentestId)) {
        const { organizationToken , pentestId, pentestName } = props.record;
        const pentestIdArray = pentestId?.split(',') || [];
        const pentestNameArray = pentestName?.split(',') || [];
        return {
          [organizationToken]: pentestIdArray.map((id, index) => ({ id, name: pentestNameArray[index] }))
        }
      }
      return {};
    });

    useEffect(() => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
        promise.then((response) => {
          const { projects } = response;
          setOrganisationList(projects);
        }).catch((err)=>{
          message.error('Problem fetching organizations')
        });
        
        return cancel;
    }, [props.loginConfigId, props.toolName]);

    const fetchPentests = useCallback((organisationToken) => {
      if((editModeRef.current && organisationToken) || (organisationToken && !pentestList[organisationToken])){
        editModeRef.current = false;
        const {promise,cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, 
          { login_id: props.loginConfigId, organisation: organisationToken});

          promise.then(response=>{
            const { projects } = response;
            setPentestList((pentestList) =>({...pentestList,[organisationToken]: projects}));
          }).catch(()=>{
          message.error('Problem fetching pentests')
          })

          return cancel;
      }
    }, [pentestList, props.loginConfigId, props.toolName]);

    useEffect(()=> fetchPentests(organisationToken), [organisationToken, fetchPentests]);
  
    const getExtraFieldsData = useCallback((config) => {
      const { organizationId, pentestId } = config;
      const project = organisationList.find(project => project.id === organizationId)??{};
      const organizationToken = project.otherProperties?.organizationToken
      const data = pentestList[organizationToken]?.reduce((acc,cv) =>{
        const { id, name } = cv;
        if(pentestId?.includes(id)){
          acc.id.push(id);
          acc.name.push(name);
        }
        return acc;
      },{id:[],name:[]})

      return {
        organizationId,
        organizationName: project.name,
        pentestId: _.isEmpty(data?.id) ? null : data.id.join(),
        pentestName:  _.isEmpty(data?.name) ? null : data.name.join(),
        organizationToken: project.otherProperties?.organizationToken,
      };
    }, [organisationList, pentestList]);

    const onOrganisationChange = useCallback((value, field) => {
      setOrganisationToken(organisationList.find(org => org.id === value).otherProperties?.organizationToken)
      const { name } = field;
      const configurations = form.getFieldValue("configurations") || [];
      let updatedConfiguration = [
        ...configurations,
      ];
      updatedConfiguration[name] = {
        ...updatedConfiguration[name],
        pentestId: undefined,
      }
      form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form, organisationList]);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { pentestId, organizationId} = config;
      return ({
          organizationId: organizationId,
          pentestId: pentestId?.split(',')
      });
  }, []);
  
  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return (!fieldConfiguration || !fieldConfiguration.organizationId);
  }, []);

    const extraFields = useMemo(() => ([{
        name: 'organizationId',
        colSpan: 10,
        type: FieldTypes.SELECT,
        label: 'Organization Name',
        options: organisationList.map(option => {
          const { label = option.name, value = option.id } = option;
          return { label, value }
        }),
        required: true,
        props: {
          autoFocus: true,
          onChange: onOrganisationChange
        }
      }, {
        name: 'pentestId',
        colSpan: 12,
        type: FieldTypes.MULTI_SELECT,
        label: 'Pentest',
        propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form)}),
        props: {
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations")
            const orgId = configs[fieldKey]?.organizationId
            const organisationToken = orgId ? organisationList.find(org => org.id === orgId).otherProperties?.organizationToken : null
            return organisationToken ? parseSelectOptions(pentestList[organisationToken]): []
          }
        }
      },
    ]), [organisationList, onOrganisationChange, pentestList,isProjectFieldDisabled, form]);
    return (
        <GenericMappingForm
          ref={ref}
          {...props}
          form={form}
          getExtraFieldsData={getExtraFieldsData}
          parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
          extraFields={extraFields}
        />
      )
})

export default CobaltMappingForm;